import { createNamespacedHelpers } from 'vuex';
import { closeCampaigns } from 'optinmonster-js-library/src/utils/omapi';
const { mapState } = createNamespacedHelpers('templates');

export const templatePreviewItem = {
	data() {
		return {
			tryCount: 0,
			campaignLoaded: false,
		};
	},
	computed: {
		...mapState(['loadingPreview', 'previewing']),
		/**
		 * Retrieves the campaign slug (for easy access).
		 *
		 * @returns {string} The campaign slug.
		 */
		slug() {
			return this.template.demo_campaign_slug;
		},
		/**
		 * Determine if we are opening the template's preview.
		 *
		 * @returns {boolean} true if we are opening the preview, false otherwise.
		 */
		isOpening() {
			return this.slug === this.loadingPreview;
		},
		/**
		 * Determine if we are currently previewing the template
		 *
		 * @returns {boolean} true if we are previewing the template, false otherwise.
		 */
		isPreviewing() {
			return this.slug === this.previewing;
		},
		/**
		 * Generate the template's Monster Campaign URL.
		 *
		 * @returns {string} The MonsterLink URL.
		 */
		monsterCampaignUrl() {
			return this.$urls.app(`c/${this.slug}`);
		},
		/**
		 * Generate the upsell URL.
		 *
		 * @returns {string} The upsell URL.
		 */
		upsellUrl() {
			return this.$urls.app(
				'account/upgrade?utm_source=app&utm_medium=upsell&utm_campaign=templates&feature=templates'
			);
		},
		imgUrl() {
			const parts = this.template.image_url.split('/campaign-templates');
			if (parts[1]) {
				return this.$urls.cdn('campaign-templates' + parts[1]);
			}

			const url = new URL(this.template.image_url);

			return this.template.image_url.replace(url.protocol + '//' + url.hostname, this.$urls.cdn());
		},
		previewText() {
			return this.campaignLoaded ? 'Preview' : 'Loading Preview...';
		},
		isInline() {
			return 'inline' === this.template.type;
		},
	},
	methods: {
		addListeners() {
			this.campaignLoaded = !!window[`om${this.slug}`];

			document.addEventListener('om.Campaign.init', this.setLoaded);
			document.addEventListener('om.Campaign.afterShow', this.afterShow);
			document.addEventListener('om.Campaign.startClose', this.startClose);
		},
		removeListeners() {
			document.removeEventListener('om.Campaign.init', this.setLoaded);
			document.removeEventListener('om.Campaign.afterShow', this.afterShow);
			document.removeEventListener('om.Campaign.startClose', this.startClose);
		},
		setLoaded(e) {
			const { Campaign: c } = e.detail;
			if (c && c.id === this.slug) {
				this.campaignLoaded = !!window[`om${this.slug}`];
			}
		},
		useTemplate() {
			this.$store.commit('templates/setActiveTemplate', this.template);
		},
		/**
		 * After Show
		 *
		 * We need to ensure that the loading preview flag is turned off
		 * once the campaign has been shown in addition to the mobile
		 * functionality that may need to take place.
		 *
		 * @param {Object} e The event.
		 *
		 * @returns {void}
		 */
		afterShow(e) {
			const { Campaign: c } = e.detail;
			if (c && c.id === this.slug) {
				this.doneLoadingPreview();
			}
		},
		/**
		 * Start Close
		 *
		 * We need to ensure that the currently previewed slug is removed
		 * once the campaign has started closing this is in addition to
		 *  the mobile functionality that may need to take place.
		 *
		 * @param {Object} e The event.
		 *
		 * @returns {void}
		 */
		startClose(e) {
			const { Campaign: c } = e.detail;
			if (c && c.id === this.slug) {
				this.donePreviewing();
			}
		},
		/**
		 * Open the preview for the Template.
		 *
		 * @param {Object} e The event.
		 *
		 * @returns {void}
		 */
		preview() {
			// If the preview is currently opening or open and we've previously tried
			// to open it, return false.
			if ((this.isOpening || this.isPreviewing) && !this.tryCount) {
				return false;
			}

			this.$store.commit('templates/setLoadingPreview', this.slug);
			this.$store.commit('templates/setPreviewing', this.slug);

			const campaign = window[`om${this.slug}`];

			if (campaign) {
				return closeCampaigns((c) => this.slug !== c.id && !c.options.mobile)
					? // If we had to close some campaigns, we need to wait for the close
					  // animations to complete before opening the new campaign.
					  campaign.Timeouts.set(() => campaign.startShow(), 200)
					: campaign.startShow();
			}

			this.fallbackOpen();
		},
		/**
		 * Fallback Open
		 *
		 * Should the initial preview method fail to open the preview,
		 * try again using this method. Should it fail, simply open
		 * the MonsterCampaign.
		 *
		 * @returns {void}
		 */
		fallbackOpen() {
			if (!this.campaignLoaded && 25 > this.tryCount) {
				this.tryCount++;
				return setTimeout(() => this.preview(), 500);
			}

			// After 20 seconds, we give up, and simply open the preview monster-campaign
			window.open(this.monsterCampaignUrl);

			setTimeout(() => {
				this.doneLoadingPreview();
				this.donePreviewing();
			}, 1000);
		},
		/**
		 * Handle the completion of loading the preview.
		 *
		 * Reset the loadingPreview flag once the preview
		 * is done loading.
		 *
		 * @returns {void}
		 */
		doneLoadingPreview() {
			if (this.isOpening) {
				this.$store.commit('templates/setLoadingPreview', '');
			}
		},
		/**
		 * Handle the completion of previewing the template.
		 *
		 * Reset the previewing flag once previewing
		 * has been completed.
		 *
		 * @returns {void}
		 */
		donePreviewing() {
			if (this.isPreviewing) {
				this.$store.commit('templates/setPreviewing', '');
			}
		},
		/**
		 * Possibly start the process of closing the preview.
		 *
		 * @param {Obect} e The event.
		 *
		 * @returns {void}
		 */
		maybeClose(e) {
			if (`campaign-${this.slug}` === e.target.id) {
				const campaign = window[`om${this.slug}`];

				if (campaign) {
					campaign.startClose(true);
				}

				this.donePreviewing();
			}
		},
	},
};
